<template>
  <v-dialog v-model="dialog" persistent max-width="700px">
    <v-card class="elevation-1">
      <v-toolbar dark color="primary">
        <v-btn icon dark @click.native="handleModal">
          <v-icon>fas fa-times-circle</v-icon>
        </v-btn>
        <v-toolbar-title>Generar reporte de venta</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
      <v-card-text>
        <v-row dense>
          <v-col cols="6">
            <SingleCalendar refElement="startDate" title="Fecha inicio" :input-date.sync="startDate" />
          </v-col>
          <v-col cols="6">
            <SingleCalendar refElement="endDate" title="Fecha fin" :input-date.sync="endDate" :min-date="startDate" />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" class="py-1">
            <v-autocomplete :items="tipoSalida" item-text="nombre" item-value="id" prepend-inner-icon="fas fa-outdent"
              hint="Selecciona el tipo de salida." v-model="tipoSalidaId" label="Tipo salida" required outlined
              multiple dense hide-details="auto">
            </v-autocomplete>
          </v-col>
          <v-col cols="12" class="py-1">
            <v-autocomplete :items="sucursales" item-text="nombre" item-value="id" v-model="sucursalId"
              label="Sucursales" required outlined prepend-inner-icon="fas fa-id-card-alt" :readonly="!canSeeAll"
              hint="Seleccione la sucursal. Este campo es opcional" dense :clearable="canSeeAll" hide-details="auto">
            </v-autocomplete>
          </v-col>
          <v-col cols="12" class="py-1">
            <v-autocomplete :items="clients" item-text="razonSocial" item-value="idCliente" v-model="clientId"
              label="Clientes" required outlined dense prepend-inner-icon="fas fa-user-alt"
              hint="Seleccione un cliente. Este campo es opcional" clearable hide-details="auto">
            </v-autocomplete>
          </v-col>
          <v-col cols="12" class="py-1" v-if="showServiceReport">
            <v-switch v-model="serviceReportMode" label="Reporte de servicios (matanza, etc.)" color="success"
              hide-details title="Folio, cliente, piezas, kg, precio unitario y una columna por cada servicio con total general por fila."></v-switch>
          </v-col>
          <v-col cols="12" class="py-1" v-if="showDetailReport && !serviceReportMode">
            <v-switch v-model="showDetail" label="Reporte detallado" color="success" hide-details></v-switch>
          </v-col>
          <v-col cols="12" class="py-1" v-if="showDetail || serviceReportMode">
            <ValidationProvider :rules="showDetail && !serviceReportMode ? 'required' : ''" v-slot="{ errors, valid}" :immediate="true">
              <v-autocomplete :items="products" item-text="nombre" item-value="id" v-model="productId"
                prepend-inner-icon="fas fa-boxes"
                :hint="showDetail && !serviceReportMode ? 'Seleccione el producto. Este campo es obligatorio si el reporte es detallado.' : 'Seleccione un producto para filtrar. Este campo es opcional.'"
                label="Producto" outlined dense :clearable="serviceReportMode" :error-messages="errors" :success="valid" hide-details="auto">
              </v-autocomplete>
            </ValidationProvider>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-4" dark @click.native="downloadExcelVentaByDay">
          <v-icon left>fas fa-file-excel</v-icon>Descargar excel
        </v-btn>
        <v-btn color="blue darken-4" dark class="btn text-white" @click="previewPDF">
          <v-icon left>fas fa-file-pdf</v-icon>Ver PDF
        </v-btn>
      </v-card-actions>
    </v-card>

    <PreviewPDF :pdfSource="pdfSource" :pdfBlob="pdfBlob" title-pdf="VENTAS" :handlePdfModal="handlePdfModal"
      :dialog="showPdf" v-if="showPdf">
    </PreviewPDF>
  </v-dialog>
</template>
<script>
import PreviewPDF from '@/components/shared/PreviewPDF.vue';
import SingleCalendar from '@/components/shared/SingleCalendar.vue';
import SucursalDataService from '@/service/catalogo/sucursal/sucursalDataService';
import ProductoDataService from '@/service/producto/ProductoDataService';
import UsuarioDataService from '@/service/usuario/UsuarioDataService';
import VentaDataService from '@/service/venta/VentaDataService';
import { getDateLocaleNatural, getToday } from '@/utils/DateUtil';

export default {
  name: 'ExcelExportVenta',
  props: {
    dialog: { type: Boolean, default: false },
    handleModal: { type: Function },
    tipoSalida: { type: Array, default: () => [] },
    sucursales: { type: Array, default: () => [] },
    clients: { type: Array, default: () => [] },
    isAdmin: { type: Boolean, default: false },
    showServiceReport: { type: Boolean, default: false },
    showDetailReport: { type: Boolean, default: false }
  },
  components: { SingleCalendar, PreviewPDF },
  created() {
    this.getProducts();
  },
  data() {
    return {
      startDate: getToday().toFormat('yyyy-MM-dd'),
      endDate: getToday().toFormat('yyyy-MM-dd'),
      tipoSalidaId: [],
      sucursalId: SucursalDataService.getCurrentSucursalId(),
      clientId: null,
      productId: null,
      showDetail: false,
      serviceReportMode: false,
      products: [],
      // to PDF
      pdfBlob: '',
      pdfSource: null,
      showPdf: false,
    }
  },
  methods: {
    async getProducts() {
      this.products = await ProductoDataService.findAll().then( resp => resp.data );
    },
    downloadExcelVentaByDay() {
      const startDate = getDateLocaleNatural(this.startDate);
      const endDate = getDateLocaleNatural(this.endDate);
      const serviceMode = this.serviceReportMode;
      this.$swal.fire({
        title: serviceMode ? '¿Quieres continuar con la descarga del reporte de servicios?' : 'Quieres continuar con la descarga del reporte de ventas?',
        text: 'El tiempo de descarga puede variar dependiendo de la cantidad de registros',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Si',
        cancelButtonText: 'No',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            if (serviceMode) {
              const params = { startDate, endDate, sucursalId: this.sucursalId, tipoSalidaId: this.tipoSalidaId,
                clientId: this.clientId, productId: this.productId
              };
              return await VentaDataService.downloadVentaServiceReport(params);
            }
            const params = { startDate, endDate, sucursalId: this.sucursalId, tipoSalidaId: this.extractTypeOutId,
              productId: this.productId, clientId: this.clientId, typeOutNameSelected: this.extractTypeOutName
            };
            return await VentaDataService.downloadVentaExcelByDay(params);
          } catch (error) {
            this.$swal.showValidationMessage(`Petición fallida: ${error}`);
          }
        },
        allowOutsideClick: () => !this.$swal.isLoading()
      }).then( result => {
        if (result.isConfirmed) {
          if( result.value.status == 204 ) {
            this.$swal.fire({ icon: 'info', title: 'Reporte', text: 'No hay registros para descargar', showConfirmButton: false, timer: 1500 });
          } else {
            const url = window.URL.createObjectURL(new Blob([result.value.data]));
            const link = document.createElement('a');
            link.href = url;
            const filename = serviceMode ? `Reporte_cerdo_servicios_${startDate}_${endDate}.xlsx` : `Ventas ${startDate} - ${endDate}.xlsx`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            this.$swal.fire({ icon: 'success', title: 'Reporte', text: 'Reporte descargado', showConfirmButton: false, timer: 1500 });
          }
        }
      }).catch(err => {
        this.$swal.fire({ position: 'top-end', icon: 'error', html: 'Error al obtener los datos' + err, showConfirmButton: false, timer: 3500 });
      })
    },
    previewPDF() {
      const startDate = getDateLocaleNatural(this.startDate) ?? '';
      const endDate = getDateLocaleNatural(this.endDate) ?? '';
      const request = this.serviceReportMode
        ? VentaDataService.printVentaServiceReport({ startDate, endDate, sucursalId: this.sucursalId,
            tipoSalidaId: this.tipoSalidaId, clientId: this.clientId, productId: this.productId })
        : VentaDataService.printSell({ startDate, endDate, sucursalId: this.sucursalId, tipoSalidaId: this.extractTypeOutId,
            productId: this.productId, clientId: this.clientId, typeOutNameSelected: this.extractTypeOutName });
      request.then( response => {
        if(response.status == 204) {
          this.$swal.fire({ icon: 'info', title: 'Reporte PDF ventas', text: 'No hay registros para visualizar', showConfirmButton: false, timer: 2500 });
          return
        }
        this.pdfBlob = new Blob([response.data], { type: 'application/pdf' })
        this.pdfSource = URL.createObjectURL(this.pdfBlob)
        this.showPdf = true
      }).catch( error => {
        this.$swal.fire({ position: 'top-end', icon: 'error', html: 'Error al generar el reporte' + (error.response?.data ?? error.message), showConfirmButton: false, timer: 3500 });
      });
    },
    handlePdfModal(){
      this.showPdf = !this.showPdf
      this.cleanupPdf()
    },
    cleanupPdf(){
      if(this.pdfSource){
        URL.revokeObjectURL(this.pdfSource)
        this.pdfSource = null
      }
      this.pdfBlob = null
    }
  },
  watch: {
    showDetail: function(val) {
      if(val == false) {
        this.productId = null
      }
    },
    serviceReportMode: function() {
      this.productId = null
      this.showDetail = false
    }
  },
  computed: {
    hasTypeOutSelected() {
      return this.tipoSalidaId.length > 0
    },
    extractTypeOutName() {
      return this.hasTypeOutSelected ? this.tipoSalida.filter(ts => this.tipoSalidaId.includes(ts.id)).map( item => item.nombre ).join(', ') : 'General'
    },
    extractTypeOutId() {
      return this.hasTypeOutSelected ? this.tipoSalidaId.join(',') : ''
    },
    canSeeAll(){
      const roles = UsuarioDataService.getUserRoles();
      return ['ROLE_COBRANZA', 'ROLE_ADMINISTRATOR_SUCURSAL'].some(role => roles.includes(role)) || this.isAdmin;
    },
  }
}
</script>